import React, { useEffect, useState } from 'react';
import { Autocomplete, TextField, CircularProgress } from '@mui/material';
import registryService from '../services/registryService';
import useDebouncedValue from '../hooks/useDebouncedValue';

/**
 * Autocomplete over the entries of a referenced registry. The selected value is
 * the referenced field's value (a string). Options are fetched (debounced) from
 * the lookup endpoint; the field is not free-solo, so a user can only pick an
 * existing value — matching the backend's existence enforcement.
 *
 * Optional pass-through props for compact, in-cell use (BoundArrayTable):
 * `size`, `autoFocus`, `openOnFocus`, `hideLabel` (label hidden, kept as aria-label).
 */
const ReferenceAutocomplete = ({
  field,
  value,
  onChange,
  readOnly = false,
  error,
  helperText,
  lookup = registryService.lookup,
  size,
  autoFocus = false,
  openOnFocus = false,
  hideLabel = false,
}) => {
  const ref = field.reference || {};
  const [inputValue, setInputValue] = useState('');
  const [options, setOptions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [hadError, setHadError] = useState(false);
  const debouncedInput = useDebouncedValue(inputValue, 300);

  useEffect(() => {
    if (!ref.registryId || !ref.field) {
      return undefined; // incomplete reference config -> nothing to look up
    }
    let active = true;
    setLoading(true);
    setHadError(false);
    Promise.resolve(lookup(ref.registryId, ref.field, debouncedInput, 10))
      .then((results) => { if (active) setOptions(Array.isArray(results) ? results : []); })
      .catch(() => { if (active) { setOptions([]); setHadError(true); } })
      .finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, [debouncedInput, ref.registryId, ref.field, lookup]);

  // Keep the current value selectable even before options load (edit case),
  // so MUI does not warn about a value that is absent from `options`.
  const mergedOptions = value && !options.includes(value) ? [value, ...options] : options;

  return (
    <Autocomplete
      value={value || null}
      onChange={(event, newValue) => onChange(newValue || '')}
      onInputChange={(event, newInput) => setInputValue(newInput)}
      options={mergedOptions}
      loading={loading}
      disabled={readOnly}
      size={size}
      openOnFocus={openOnFocus}
      noOptionsText={!loading && hadError ? 'Errore nel caricamento' : 'Nessun risultato'}
      isOptionEqualToValue={(option, val) => option === val}
      renderInput={(params) => (
        <TextField
          {...params}
          label={hideLabel ? undefined : field.name}
          autoFocus={autoFocus}
          error={!!error}
          helperText={error || helperText || field.description}
          inputProps={{
            ...params.inputProps,
            ...(hideLabel ? { 'aria-label': field.name } : {}),
          }}
          InputProps={{
            ...params.InputProps,
            endAdornment: (
              <>
                {loading ? <CircularProgress color="inherit" size={20} /> : null}
                {params.InputProps.endAdornment}
              </>
            ),
          }}
        />
      )}
    />
  );
};

export default ReferenceAutocomplete;
